import type { JSONContent } from '@tiptap/core';

/**
 * Marker format for custom icons in markdown:
 *   FA icon:    {{icon:fa:name:color}}
 *   Image icon: {{icon:img:src}}
 */

const CUSTOM_ICON_REGEX = /\{\{icon:(fa|img):([^:}]+)(?::([^}]*))?\}\}/g;

interface CustomIconNodeAttrs {
  iconType?: 'fa' | 'img';
  name?: string;
  color?: string;
}

// --- Save path: CustomIcon nodes → text markers ---

/**
 * Recursively converts customIcon nodes to text nodes containing marker syntax.
 * This allows customIcon nodes to survive serialization through the MarkdownManager,
 * which doesn't include the CustomIcon extension.
 */
export function convertCustomIconNodesToText(node: JSONContent): JSONContent {
  if (node.type === 'customIcon') {
    return convertCustomIconNodeToTextNode(node);
  }

  if (!node.content) {
    return node;
  }

  return {
    ...node,
    content: convertCustomIconNodesInContent(node.content),
  };
}

function convertCustomIconNodeToTextNode(node: JSONContent): JSONContent {
  const attrs = node.attrs as CustomIconNodeAttrs | undefined;
  const iconType = attrs?.iconType ?? 'fa';
  const name = attrs?.name ?? '';

  let marker: string;
  if (iconType === 'fa') {
    const color = attrs?.color ?? 'currentColor';
    marker = `{{icon:fa:${name}:${color}}}`;
  } else {
    marker = `{{icon:img:${name}}}`;
  }

  return { type: 'text', text: marker, ...(node.marks && { marks: node.marks }) };
}

function convertCustomIconNodesInContent(content: JSONContent[]): JSONContent[] {
  const result: JSONContent[] = [];

  for (const node of content) {
    if (node.type === 'customIcon') {
      result.push(convertCustomIconNodeToTextNode(node));
    } else if (node.content) {
      result.push(convertCustomIconNodesToText(node));
    } else {
      result.push(node);
    }
  }

  return result;
}

// --- Load path: text markers → CustomIcon nodes ---

/**
 * Recursively processes a node to convert {{icon:...}} markers in text to customIcon nodes.
 */
export function processNodeForCustomIcons(node: JSONContent): JSONContent {
  if (node.content) {
    return {
      ...node,
      content: processContentForCustomIcons(node.content),
    };
  }

  return node;
}

function processContentForCustomIcons(content: JSONContent[]): JSONContent[] {
  const result: JSONContent[] = [];

  for (const node of content) {
    if (node.type === 'text' && node.text) {
      const parts = splitTextWithCustomIcons(node.text, node.marks);
      result.push(...parts);
    } else if (node.content) {
      result.push({
        ...node,
        content: processContentForCustomIcons(node.content),
      });
    } else {
      result.push(node);
    }
  }

  return result;
}

function splitTextWithCustomIcons(text: string, marks?: JSONContent['marks']): JSONContent[] {
  const result: JSONContent[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(CUSTOM_ICON_REGEX)) {
    // Add text before the match
    if (match.index > lastIndex) {
      const beforeText = text.slice(lastIndex, match.index);
      result.push({ type: 'text', text: beforeText, ...(marks && { marks }) });
    }

    const iconType = match[1] as 'fa' | 'img';
    const name = match[2];
    const color = match[3];

    const attrs: CustomIconNodeAttrs = { iconType, name };
    if (iconType === 'fa' && color) {
      attrs.color = color;
    }

    result.push({
      type: 'customIcon',
      attrs,
      ...(marks && { marks }),
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    result.push({ type: 'text', text: remainingText, ...(marks && { marks }) });
  }

  // If no icons were found, return original text node
  if (result.length === 0) {
    result.push({ type: 'text', text, ...(marks && { marks }) });
  }

  return result;
}
